// src/utils/currencyHelper.js
// Currency formatting utilities with comma support for legal accuracy

/**
 * Convert dollars to cents
 * @param {number} dollars - Dollar amount
 * @returns {number} Amount in cents
 */
export function toCents(dollars) {
  return Math.max(0, Math.round(Number(dollars || 0) * 100));
}

/**
 * Format cents as USD with commas
 * Examples:
 * - 10000 → "$100.00"
 * - 165888 → "$1,658.88"
 * - 1000000 → "$10,000.00"
 * - 100000000 → "$1,000,000.00"
 * 
 * @param {number} cents - Amount in cents
 * @param {boolean} hideCents - If true, omit cents for whole dollar amounts
 * @returns {string} Formatted currency string with commas
 */
export function fmtUSD(cents, hideCents = false) {
  const dollars = Number(cents || 0) / 100;
  
  if (hideCents && dollars === Math.floor(dollars)) {
    // Whole dollar amount, no decimal places
    return `$${Math.floor(dollars).toLocaleString('en-US')}`;
  }
  
  // Always show 2 decimal places
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}

/**
 * Format cents as compact currency (K/M notation) with legal accuracy
 * ALWAYS rounds DOWN to avoid overstating prize amounts
 * 
 * Examples:
 * - 165888 cents ($1,658.88) → "$1.6K"
 * - 200000 cents ($2,000.00) → "$2K"
 * - 195000 cents ($1,950.00) → "$1.9K"
 * - 50000 cents ($500.00) → "$500"
 * - 120000000 cents ($1,200,000) → "$1.2M"
 * 
 * @param {number} cents - Amount in cents
 * @returns {string} Compact formatted currency
 */
export function fmtCompactUSD(cents) {
  const dollars = Math.floor(Number(cents || 0) / 100);
  
  // Millions: floor to nearest $100k
  if (dollars >= 1_000_000) {
    const millions = Math.floor(dollars / 100_000) / 10;
    return `$${millions.toFixed(1).replace(/\.0$/, '')}M`;
  }
  
  // Thousands: floor to nearest $100
  if (dollars >= 1_000) {
    const thousands = Math.floor(dollars / 100) / 10;
    return `$${thousands.toFixed(1).replace(/\.0$/, '')}K`;
  }
  
  // Under $1,000: show exact amount with commas
  return `$${dollars.toLocaleString('en-US')}`;
}

/**
 * Format a whole-Crit count for display (post-pivot play currency).
 * NOTE: Crits are WHOLE UNITS, not cents — pass the Crit count directly.
 * Examples: 1 → "1 Crit"; 40 → "40 Crits"; 1500 → "1,500 Crits".
 * @param {number} units - number of Crits
 * @returns {string}
 */
export function fmtCrits(units) {
  const n = Math.max(0, Math.floor(Number(units || 0)));
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'Crit' : 'Crits'}`;
}

/**
 * Compact Crit formatter (K/M), floors down to avoid overstating.
 * Examples: 1500 → "1.5K Crits"; 1200000 → "1.2M Crits"; 500 → "500 Crits".
 * @param {number} units - number of Crits
 * @returns {string}
 */
export function fmtCompactCrits(units) {
  const n = Math.max(0, Math.floor(Number(units || 0)));
  if (n >= 1_000_000) {
    const m = Math.floor(n / 100_000) / 10;
    return `${m.toFixed(1).replace(/\.0$/, '')}M Crits`;
  }
  if (n >= 1_000) {
    const k = Math.floor(n / 100) / 10;
    return `${k.toFixed(1).replace(/\.0$/, '')}K Crits`;
  }
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'Crit' : 'Crits'}`;
}

/**
 * Format number with commas (no currency symbol)
 * Examples:
 * - 1000 → "1,000"
 * - 1658.88 → "1,658.88"
 * - 1000000 → "1,000,000"
 * 
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number with commas
 */
export function fmtNumber(num, decimals = 0) {
  return Number(num || 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * Parse currency string to cents
 * Handles formats like: "$100", "$1,000.00", "100", "1000"
 * 
 * @param {string} str - Currency string
 * @returns {number} Amount in cents
 */
export function parseCurrency(str) {
  if (!str) return 0;
  
  // Remove currency symbols and commas
  const cleaned = String(str).replace(/[$,\s]/g, '');
  const dollars = parseFloat(cleaned) || 0;
  
  return Math.max(0, Math.round(dollars * 100));
}

// Example usage:
// 
// import { fmtUSD, fmtCompactUSD, fmtNumber, toCents } from '../utils/currencyHelper';
// 
// const prizeCents = 165888; // $1,658.88
// 
// console.log(fmtUSD(prizeCents));           // "$1,658.88"
// console.log(fmtUSD(prizeCents, true));     // "$1,659" (if it were a whole dollar amount)
// console.log(fmtCompactUSD(prizeCents));    // "$1.6K"
// 
// const entryFee = toCents(5);               // 500 cents
// console.log(fmtUSD(entryFee));             // "$5.00"
// 
// const largePool = 10000000;                // $100,000.00
// console.log(fmtUSD(largePool));            // "$100,000.00"
// console.log(fmtCompactUSD(largePool));     // "$100K"